import { StoreSelector } from "@/components/menu/StoreSelector";
import { OrderDetailsDialog } from "@/components/orders/OrderDetailsDialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useAuthStore } from "@/store/authStore";
import { useOrderStore } from "@/store/orderStore";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { Order } from "@/types/orders";
import { format } from "date-fns";
import { Filter } from "lucide-react";
import { useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { OrderHistoryItem } from "@/components/orders/OrderHistoryItem";

const OrderHistory = () => {
  const { t } = useTranslation();

  const {
    orders,
    fetchOrders,
    setSelectedStoreId,
    subscribeToOrdersRealtime,
    unsubscribeFromOrdersRealtime,
    deleteOrder,
  } = useOrderStore();

  const { user } = useAuthStore();
  const { stores, selectedStoreId } = useStoreManagementStore();

  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });
  const [isFiltered, setIsFiltered] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  // Calculate totals for the stats cards
  const getTotalOrdersToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return orders.filter((order) => {
      const orderDate = new Date(order.createdAt);
      return orderDate >= today;
    }).length;
  };

  const getTotalCancelledOrdersToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return orders.filter((order) => {
      const orderDate = new Date(order.createdAt);
      return orderDate >= today && order.status === "cancelled";
    }).length;
  };

  const getTotalOrderAmount = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return orders
      .filter((order) => {
        const orderDate = new Date(order.createdAt);
        return orderDate >= today && order.status !== "cancelled";
      })
      .reduce((sum, order) => sum + order.totalAmount, 0);
  };

  useEffect(() => {
    // Fetch stores first
    useStoreManagementStore.getState().fetchStores();
  }, []);

  useEffect(() => {
    if (user && user.role) {
      // If user is staff and has access to only one store, preselect it and disable selection
      if (
        user.role === "staff" &&
        user.storeIds &&
        user.storeIds.length === 1
      ) {
        setSelectedStoreId(user.storeIds[0]);
      }
    }
  }, [user, setSelectedStoreId]);

  useEffect(() => {
    // Fetch orders whenever the selected store changes
    if (selectedStoreId) {
      fetchOrders(selectedStoreId);

      // Subscribe to real-time order updates
      console.log(
        "OrderHistory: Subscribing to real-time order updates for store:",
        selectedStoreId
      );
      subscribeToOrdersRealtime(selectedStoreId);

      // Cleanup subscription when component unmounts or store changes
      return () => {
        console.log("OrderHistory: Cleaning up real-time subscription");
        unsubscribeFromOrdersRealtime();
      };
    }
  }, [
    selectedStoreId,
    fetchOrders,
    subscribeToOrdersRealtime,
    unsubscribeFromOrdersRealtime,
  ]);

  // Log whenever orders are updated
  useEffect(() => {
    console.log("OrderHistory - Orders updated:", orders.length, "orders");
  }, [orders]);

  const handleOrderClick = (orderId: string) => {
    setSelectedOrderId(orderId);
    setShowDetailsDialog(true);
  };

  const handleApplyFilter = () => {
    setIsFiltered(!!dateRange.from);
    setShowFilterDialog(false);
  };

  const handleClearFilter = () => {
    setDateRange({ from: undefined, to: undefined });
    setIsFiltered(false);
    setShowFilterDialog(false);
  };

  const selectedOrder =
    orders.find((order) => order.id === selectedOrderId) || null;

  // Filter orders by date range if filtering is active
  const dateFilteredOrders = isFiltered
    ? orders.filter((order) => {
        const orderDate = new Date(order.createdAt);
        if (dateRange.from && orderDate < dateRange.from) return false;
        if (
          dateRange.to &&
          orderDate > new Date(dateRange.to.setHours(23, 59, 59, 999))
        )
          return false;
        return true;
      })
    : orders;

  // Now ensure we correctly filter by status for each tab
  const pendingOrders = dateFilteredOrders.filter(
    (order) => order.status === "pending" || order.status === "preparing"
  );
  const preparingOrders = dateFilteredOrders.filter(
    (order) => order.status === "preparing"
  );
  const readyOrders = dateFilteredOrders.filter(
    (order) => order.status === "ready"
  );
  const deliveredOrders = dateFilteredOrders.filter(
    (order) => order.status === "delivered"
  );
  const cancelledOrders = dateFilteredOrders.filter(
    (order) => order.status === "cancelled"
  );

  // Determine if store selection should be disabled
  const isStoreSelectionDisabled = user?.role === "staff";

  // Format date range for display
  const dateRangeText = dateRange.from
    ? `${format(dateRange.from, "MMM dd, yyyy")}${
        dateRange.to ? ` - ${format(dateRange.to, "MMM dd, yyyy")}` : ""
      }`
    : t("allDates");

  // Render orders list with simplified OrderHistoryItem component
  const renderOrders = (ordersList: Order[]) => {
    if (ordersList.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          {t("noOrdersFound")}
        </div>
      );
    }

    return (
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {ordersList.map((order) => (
          <OrderHistoryItem
            key={order.id}
            order={order}
            onClick={() => handleOrderClick(order.id)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t("orderHistory")}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t("viewAndAnalyzeOrders")}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilterDialog(true)}
            className="flex gap-2 items-center"
          >
            <Filter className="h-4 w-4" />
            <span className="hidden md:inline">{t("filterOrdersByDate")}</span>
            <span className="md:hidden">{t("filterOrdersByDate")}</span>
            {isFiltered && (
              <span className="ml-2 h-2 w-2 rounded-full bg-primary"></span>
            )}
          </Button>
        </div>
      </div>

      {selectedStoreId && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>{t("todaysOrders")}</CardTitle>
              <CardDescription>{t("totalOrdersReceivedToday")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{getTotalOrdersToday()}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle>{t("cancelledOrders")}</CardTitle>
              <CardDescription>{t("ordersCancelledToday")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {getTotalCancelledOrdersToday()}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle>{t("totalSales")}</CardTitle>
              <CardDescription>{t("currentDailyRevenue")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                ${getTotalOrderAmount().toFixed(2)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle>{t("dateFilter")}</CardTitle>
              <CardDescription>{t("currentDateRangeFilter")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-sm font-medium">{dateRangeText}</div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="space-y-4">
        <div className="flex overflow-auto pb-1 -mx-2 px-2">
          <div className="flex border-b border-muted w-full min-w-max">
            <button
              className={`px-3 py-2 text-sm transition-colors whitespace-nowrap ${
                activeTab === "all"
                  ? "border-b-2 border-primary font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setActiveTab("all")}
            >
              {t("tabs.all")}
            </button>
            <button
              className={`px-3 py-2 text-sm transition-colors whitespace-nowrap ${
                activeTab === "pending"
                  ? "border-b-2 border-primary font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setActiveTab("pending")}
            >
              {t("tabs.pending")}
            </button>
            <button
              className={`px-3 py-2 text-sm transition-colors whitespace-nowrap ${
                activeTab === "preparing"
                  ? "border-b-2 border-primary font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setActiveTab("preparing")}
            >
              {t("tabs.preparing")}
            </button>
            <button
              className={`px-3 py-2 text-sm transition-colors whitespace-nowrap ${
                activeTab === "ready"
                  ? "border-b-2 border-primary font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setActiveTab("ready")}
            >
              {t("tabs.ready")}
            </button>
            <button
              className={`px-3 py-2 text-sm transition-colors whitespace-nowrap ${
                activeTab === "delivered"
                  ? "border-b-2 border-primary font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setActiveTab("delivered")}
            >
              {t("tabs.delivered")}
            </button>
            <button
              className={`px-3 py-2 text-sm transition-colors whitespace-nowrap ${
                activeTab === "cancelled"
                  ? "border-b-2 border-primary font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setActiveTab("cancelled")}
            >
              {t("tabs.cancelled")}
            </button>
          </div>
        </div>

        {activeTab === "all" && (
          <div className="space-y-4">
            {renderOrders(dateFilteredOrders)}
          </div>
        )}

        {activeTab === "pending" && (
          <div className="space-y-4">
            {renderOrders(pendingOrders)}
          </div>
        )}

        {activeTab === "preparing" && (
          <div className="space-y-4">
            {renderOrders(preparingOrders)}
          </div>
        )}

        {activeTab === "ready" && (
          <div className="space-y-4">
            {renderOrders(readyOrders)}
          </div>
        )}

        {activeTab === "delivered" && (
          <div className="space-y-4">
            {renderOrders(deliveredOrders)}
          </div>
        )}

        {activeTab === "cancelled" && (
          <div className="space-y-4">
            {renderOrders(cancelledOrders)}
          </div>
        )}
      </div>

      {/* Date Range Filter Dialog */}
      <Dialog open={showFilterDialog} onOpenChange={setShowFilterDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("filterOrdersByDate")}</DialogTitle>
            <DialogDescription>
              {t("selectDateRangeToFilterOrders")}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={setDateRange}
                  disabled={(date) => {
                    // Disable future dates
                    return date > new Date();
                  }}
                  className="rounded-md border shadow pointer-events-auto"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClearFilter}>
              {t("reset")}
            </Button>
            <Button onClick={handleApplyFilter}>{t("applyFilter")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order details dialog */}
      <OrderDetailsDialog
        open={showDetailsDialog}
        onOpenChange={setShowDetailsDialog}
        order={selectedOrder}
        viewOnly={true} // Set viewOnly to true for OrderHistory page
      />
    </div>
  );
};

export default OrderHistory;
